/**
 * Email suppression types — Phase 1 of the email-suppression rollout
 * (2026-05-16). See database/migrations/2026-05-16-001-email-suppression-
 * phase-1.sql for the source-of-truth schema + reason vocabulary.
 *
 * is_suppressed is the user-facing flag. Rows can exist with
 * is_suppressed=false when a soft-bounce count is tracked but the
 * threshold (3) hasn't been reached yet — those rows surface as
 * yellow "Soft bounce N/3" warnings on contact detail.
 */

import type { UUID } from "./common.types";

export type EmailSuppressionReason =
  | "unsubscribed"
  | "hard_bounced"
  | "soft_bounce_threshold"
  | "spam_reported"
  | "manual";

export type EmailSuppressionSource = "sendgrid_webhook" | "manual" | "import";

/**
 * Human-readable label for a suppression reason. Used by the contact
 * detail badge and the segment-criteria dropdown.
 */
export const SUPPRESSION_REASON_LABELS: Record<EmailSuppressionReason, string> =
  {
    unsubscribed: "Unsubscribed",
    hard_bounced: "Hard bounced",
    soft_bounce_threshold: "Soft bounce (threshold reached)",
    spam_reported: "Spam reported",
    manual: "Manual suppression",
  };

export interface EmailSuppression {
  email_lower: string;
  is_suppressed: boolean;
  reason: EmailSuppressionReason | null;
  reason_detail: string | null;
  first_suppressed_at: string | null;
  last_event_at: string;
  soft_bounce_count: number;
  source: EmailSuppressionSource;
  suppressed_by: UUID | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/** Soft-bounce threshold above which is_suppressed flips to true. */
export const SOFT_BOUNCE_THRESHOLD = 3;
